import { DialogContentText } from '@mui/material';

import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import type { Or2DialogProps } from '@/components/feedback/Or2Dialog';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { graphql, type DocumentType } from '@/gql';

import { Or2Button } from '@/components/button/Or2Button';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import { addNewFileToCache } from '@/modules/files/utils/fileUpdateFunctions';

import type { UrlFileFormData } from '../forms/UrlFileForm';
import { UrlFileForm, UrlFileFormSchema } from '../forms/UrlFileForm';

const CREATE_URL_FILE_MUTATION = graphql(`
  mutation CreateUrlFile($data: CreateUrlFileInput) {
    createUrlFile(params: $data) {
      file {
        ...fileFields
      }
    }
  }
`);

const CREATE_URL_FOLDER_DIALOG_FOLDER_FRAGMENT = graphql(`
  fragment createUrlFileDialogFolderFields on File {
    ...urlFileFormParentFields
    owner {
      ... on Group {
        _id
      }
      ... on Service {
        _id
      }
    }
  }
`);

const CREATE_URL_FILE_DIALOG_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment createUrlFileDialogFilesForOwnerFields on FilesForOwner {
    ...urlFileFormFilesForOwnerFields
  }
`);

interface Props extends Omit<Or2DialogProps, 'title'> {
  folder: DocumentType<typeof CREATE_URL_FOLDER_DIALOG_FOLDER_FRAGMENT> | null;
  filesForOwner: DocumentType<
    typeof CREATE_URL_FILE_DIALOG_FILES_FOR_OWNER_FRAGMENT
  >;
  rootOwnerId: string;
}

export const CreateUrlFileDialog = ({
  folder,
  filesForOwner,
  onClose,
  rootOwnerId,
  ...other
}: Props) => {
  const { control, handleSubmit, setValue } = useForm<UrlFileFormData>({
    defaultValues: {
      filename: '',
      url: '',
    },
    resolver: zodResolver(UrlFileFormSchema),
  });

  const [createUrlFile, { loading }] = useMutation(CREATE_URL_FILE_MUTATION, {
    onCompleted: onClose,
    update(cache, { data }) {
      const newFile = data?.createUrlFile?.file;

      addNewFileToCache(cache, newFile, rootOwnerId);
    },
  });

  const onSubmit = (data: UrlFileFormData) => {
    createUrlFile({
      variables: {
        data: {
          filename: data.filename,
          url: data.url,
          parent: folder?._id ?? null,
          owner: folder?.owner?._id ?? rootOwnerId ?? null,
          type: filesForOwner.owner.__typename,
        },
      },
    });
  };

  return (
    <Or2Dialog
      maxWidth="sm"
      {...other}
      loading={loading}
      onClose={onClose}
      title={`Add a web link into ${folder?.filename ?? filesForOwner.owner.name}`}
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Close
          </Or2Button>
          <Or2Button onClick={handleSubmit(onSubmit)}>Create</Or2Button>
        </>
      }
    >
      <DialogContentText>
        Add a link to another folder or document, or to an external webpage. The
        link should start with &quot;https://&quot;.
      </DialogContentText>
      <UrlFileForm
        parent={folder}
        filesForOwner={filesForOwner}
        control={control}
        setValue={setValue}
      />
    </Or2Dialog>
  );
};
